import {
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
  add,
  sub,
} from 'date-fns'
import { toDate } from 'date-fns-tz'

import { useFindProfile } from './profile'
import { useFindSettings } from './settings'

interface IDateFormats {
  short: string
  long: string
  monthYear: string
}

interface IFormattedResult {
  dateShort: string
  dateLong: string
  dateMonthYear: string
  time?: string
}

interface IWeekList {
  date: Date | null
  weekDay: string
}

const timeZoneMapping = [
  { iana: 'Australia/Sydney', windows: 'AUS Eastern Standard Time' },
  { iana: 'Europe/Warsaw', windows: 'Central European Standard Time' },
  { iana: 'America/Chicago', windows: 'Central Standard Time' },
  { iana: 'America/New_York', windows: 'Eastern Standard Time' },
  { iana: 'Europe/London', windows: 'GMT Standard Time' },
  { iana: 'America/Denver', windows: 'Mountain Standard Time' },
  { iana: 'Europe/Paris', windows: 'Romance Standard Time' },
  { iana: 'Asia/Bangkok', windows: 'SE Asia Standard Time' },
  { iana: 'Asia/Singapore', windows: 'Singapore Standard Time' },
  { iana: 'Europe/Berlin', windows: 'W. Europe Standard Time' },
]

const dateFormats: IDateFormats[] = [
  {
    short: 'MM/dd/yyyy',
    long: 'MMM do, yyyy',
    monthYear: 'MMM yyyy',
  },
  {
    short: 'dd-MM-yyyy',
    long: 'dd MMM yyyy',
    monthYear: 'MMM yyyy',
  },
  {
    short: 'yyyy-MM-dd',
    long: 'yyyy MMM dd',
    monthYear: 'MMM yyyy',
  },
  {
    short: 'MM/dd/yyyy',
    long: 'MMM do, yyyy',
    monthYear: 'MMM yyyy',
  },
]

export const useDate = () => {
  const { data: user } = useFindProfile()
  const { data: settings } = useFindSettings()

  const siteDateFormat = user ? user.selectedSite.dateFormat : 0
  const siteTimeFormat = user ? user.selectedSite.timeFormat : 0

  const getSiteDateFormat = (): IDateFormats => {
    return dateFormats[siteDateFormat]
  }

  const formatDate = (date: Date): IFormattedResult => {
    const timeFormats = ['hh:mm a', 'hh:mm a', 'HH:mm']

    const { short, long, monthYear } = dateFormats[siteDateFormat]
    const timeFormat = timeFormats[siteTimeFormat]

    const dateShort = format(date, short)
    const dateLong = format(date, long)
    const dateMonthYear = format(date, monthYear)
    const time = format(date, timeFormat)

    return { dateShort, dateLong, dateMonthYear, time }
  }

  const formatRange = (start: Date, end: Date): string => {
    const formats = [
      {
        sameMonth: ['MMM do', 'do, yyyy'],
        differentMonth: ['MMM do', 'MMM do, yyyy'],
      },
      {
        sameMonth: ['MMM dd', 'dd, yyyy'],
        differentMonth: ['dd MMM', 'dd MMM yyyy'],
      },
      {
        sameMonth: ['yyyy MMM dd', 'dd'],
        differentMonth: ['yyyy MMM dd', 'MMM dd'],
      },
      {
        sameMonth: ['MMM do', 'do, yyyy'],
        differentMonth: ['MMM do', 'MMM do, yyyy'],
      },
    ]

    const isSameMonth = start.getMonth() === end.getMonth()
    const { sameMonth, differentMonth } = formats[siteDateFormat]
    const stringFormats = isSameMonth ? sameMonth : differentMonth

    return `${format(start, stringFormats[0])} to ${format(
      end,
      stringFormats[1],
    )}`
  }

  const getFirstAndLastDay = (date: Date): [Date, Date] => {
    const startDate = startOfMonth(date)
    const endDate = endOfMonth(date)
    return [startDate, endDate]
  }

  const getWeekDayName = (date: Date) => {
    return format(date, 'EEEE')
  }

  const moveForward = (date: Date, duration: Duration): Date => {
    return add(date, duration)
  }

  const moveBackward = (date: Date, duration: Duration): Date => {
    return sub(date, duration)
  }

  const getWeekRange = (date: Date): { weekStart: Date; weekEnd: Date } => {
    const weekStart = settings?.site.startOfWeek

    return {
      weekStart: startOfWeek(date, { weekStartsOn: weekStart }),
      weekEnd: endOfWeek(date, { weekStartsOn: weekStart }),
    }
  }

  const getWeekList = (localEnvDate: Date) => {
    const localWeekList: IWeekList[] = []

    const { weekStart } = getWeekRange(localEnvDate)
    for (let i = 0; i < 7; i++) {
      const date = moveForward(weekStart, { days: i })
      const weekDay = format(date, 'iii')

      localWeekList.push({ date, weekDay })
    }

    return localWeekList
  }

  const formatToUtc = (localEnvDate: Date) => localEnvDate.toISOString()

  const setDateTimeZone = (localEnvDate: Date) => {
    const siteTimeZoneName = user?.selectedSite.timeZoneName

    const { iana } =
      timeZoneMapping.find((tz) => tz.windows === siteTimeZoneName) ??
      timeZoneMapping[0]

    const formatDate = format(localEnvDate, 'yyyy-MM-dd HH:mm:ss.SSS')
    const dateTimeZone = toDate(formatDate, { timeZone: iana })

    return dateTimeZone
  }

  const getSiteStartTime = (startTimeHour: number, startTime: Date) => {
    let utcOffset = 0

    if (user) {
      utcOffset = user.selectedSite.utcOffset
    }

    let siteStartTimeHour =
      startTimeHour - utcOffset - startTime.getTimezoneOffset() / 60

    if (siteStartTimeHour < 0) {
      siteStartTimeHour = siteStartTimeHour + 24
    }

    const todoStartTime = new Date(startTime)
    todoStartTime.setHours(siteStartTimeHour)

    return todoStartTime
  }

  const isDateBetweenRange = (date: Date, start: Date, end: Date) => {
    return new Date(date) > new Date(start) && new Date(date) < new Date(end)
  }

  function getWeekDayBasedOnNumber(startOfWeek: string) {
    const daysOfWeek = [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Sunday',
    ]
    const dayIndex = parseInt(startOfWeek, 10) - 1
    return daysOfWeek[dayIndex] || 'Invalid day'
  }

  return {
    formatDate,
    formatRange,
    formatToUtc,
    getFirstAndLastDay,
    getWeekRange,
    getWeekDayName,
    getWeekList,
    moveForward,
    moveBackward,
    setDateTimeZone,
    getSiteStartTime,
    getSiteDateFormat,
    isDateBetweenRange,
    getWeekDayBasedOnNumber,
  }
}
